import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { isNil, uniq } from "lodash";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { IMapGeometry, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { SpatialService } from "~app/common/components/map/services/spatial.service";
import { VkKartaLayerNames } from "~app/common/models/constants";
import { EntityDTO } from "~app/common/models/entity-models";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IKartgeometriEvent } from "~app/features/map/popups/kartgeometri-popup/kartgeometri-popup.component";
import { IKartgeometri, Kartgeometri } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { VirkeskopRitunderlagKoordinaterDTO } from "~app/features/virkeskop/dto/dokument/VirkeskopRitunderlagKoordinaterDTO";
import { Atgard } from "../models/atgard-models";
import {
    Geometri,
    GeometriParentType,
    Geometrier,
    GeometryUpdate,
    IHoverMapObject,
    ISkogsvardVirtualLayer,
    VKGeometrierDTO
} from "../models/karta-models";
import { Kontrakt } from "../models/kontrakt-models";
import { VKAtgardHelper } from "../utils/vkatgard-helper";
import { AvtalService } from "./avtal.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";
import { KontraktRitunderlagService } from "./kontrakt-ritunderlag.service";

@Injectable()
export class VkKartaMediatorService implements OnDestroy {
    activeSkogsvardVirtualLayer$ = new BehaviorSubject<ISkogsvardVirtualLayer>(null);
    atgardAdded$ = new BehaviorSubject<Geometri>(null);
    atgardDrivningspunktAdded$ = new BehaviorSubject<Geometri>(null);
    atgardDrivningspunktChanged$ = new BehaviorSubject<Geometri>(null);
    atgardDrivningspunktDeleted$ = new BehaviorSubject<Geometri>(null);
    atgardGeometriAdded$ = new BehaviorSubject<Geometri>(null);
    atgardGeometriChanged$ = new BehaviorSubject<Geometri>(null);
    atgardGeometriDeleted$ = new BehaviorSubject<Geometri>(null);
    atgardGeometrier$ = new BehaviorSubject<Geometri[]>(null);
    atgardHoverIn$ = new BehaviorSubject<string>(null);
    atgardHoverOut$ = new BehaviorSubject<string>(null);
    highlight$ = new BehaviorSubject<IHoverMapObject>(null);
    kontraktGeometriAdded$ = new BehaviorSubject<Geometri>(null);
    kontraktGeometriChanged$ = new BehaviorSubject<Geometri>(null);
    kontraktGeometriDeleted$ = new BehaviorSubject<Geometri>(null);
    kontraktGeometrier$ = new BehaviorSubject<Geometri[]>(null);
    skogsvardGeometrier$ = new BehaviorSubject<Geometri[]>(null);

    private readonly avtalService = inject(AvtalService);
    private readonly destroy$ = new Subject<void>();
    private readonly recalcAreaSrc = new Subject<Geometri>();
    private readonly geometrierCache: Map<string, Geometrier> = new Map<string, Geometrier>();
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktRitunderlagService = inject(KontraktRitunderlagService);
    private readonly spatialService = inject(SpatialService);
    private readonly ngZone = inject(NgZone);
    private readonly popupGeometriSrc = new BehaviorSubject<IMapGeometry>(null);
    private readonly ritunderlagKoordinaterCache: Map<number, VirkeskopRitunderlagKoordinaterDTO> = new Map<number, VirkeskopRitunderlagKoordinaterDTO>();
    private readonly selectedSkogsvardGeometrySrc = new BehaviorSubject<IMapGeometry>(null);

    private atgarder: Atgard[];

    constructor() {
        this.kontraktMediatorService.atgarder$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => this.setAtgarder(x));
        this.kontraktMediatorService.atgardAdded$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => this.addAtgard(x));

        this.recalcAreaSrc.pipe(
            takeUntil(this.destroy$)
        ).subscribe(geo => this.recalcHektar(geo));

        this.kontraktMediatorService.kontaktGeometrierToImport$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(x => {
            if (x) {
                x.forEach(y => this.updateKontraktGeometryInCacheAndSignal(this.kontraktMediatorService.kontrakt.guid, y.guid, y.geometri, y));
            }

        });
    }

    get popupGeometri$() {
        return this.popupGeometriSrc.asObservable();
    }

    get selectedSkogsvardGeometry$(): Observable<IMapGeometry> {
        return this.selectedSkogsvardGeometrySrc.asObservable();
    }

    activateVirtualSkogsvardLayer(virtualLayer: ISkogsvardVirtualLayer) {
        this.activeSkogsvardVirtualLayer$.next(virtualLayer);
    }

    addAtgardDrivningspunktGeometri(kontraktGuid: string, geometryGuid: string, geometriTyp: string, mapGeometry: IMapGeometryWicket, atgard: Geometri): Geometri {
        return this.createKontraktGeometri(kontraktGuid, geometryGuid, geometriTyp, null, mapGeometry, atgard.guid);
    }

    addKontraktGeometri(kontraktGuid: string, geometryGuid: string, geometriTyp: string, kartGeometri?: IKartgeometri, mapGeometry?: IMapGeometryWicket): Geometri {
        return this.createKontraktGeometri(kontraktGuid, geometryGuid, geometriTyp, kartGeometri, mapGeometry);
    }

    copyAtgardToSkogsvardAtgardGeometri(kontraktGuid: string, sourceWkt: string, targetGuid: string) {
        const skogsvardGeometry = this.findAtgardGeometri(kontraktGuid, targetGuid, targetGuid);

        if (isNil(skogsvardGeometry.mapGeometry)) {
            skogsvardGeometry.setNewGeometri(
                {
                    visible: true,
                    id: targetGuid,
                    wkt: sourceWkt,
                    isNew: true,
                    styleName: "atgardYta",
                    tag: skogsvardGeometry.kartgeometri
                },
                skogsvardGeometry.kartgeometri
            );
        } else {
            skogsvardGeometry.mapGeometry.wkt = sourceWkt;
            skogsvardGeometry.mapGeometry.id = targetGuid;
        }

        this.atgardGeometriAdded$.next(skogsvardGeometry);

        this.updateGeometry(VkKartaLayerNames.skogsvardatgardLayer,
            kontraktGuid,
            targetGuid,
            targetGuid,
            sourceWkt,
            null);
    }

    deleteGeometry(layerId: string, kontraktId: string, geometryId: string) {
        this.deleteGeometryFromCacheAndSignal(kontraktId, geometryId, layerId);
    }

    deleteGeometryByEvent(event: IKartgeometriEvent): void {
        this.deleteGeometryFromCacheAndSignal(event.payload.param.kontraktId,
            event.payload.param.geometryId,
            event.payload.layer.id);
    }

    deselectSkogsvardGeometry() {
        this.selectedSkogsvardGeometrySrc.next(null);
    }

    getAllDrivningspunkterForAtgard(kontraktGuid: string, atgardGuid: string): Geometri[] {
        return this.geometrierCache
            .get(kontraktGuid).atgardGeometrier
            .find(x => x.atgardGuid === atgardGuid)?.atgardDrivningspunkter;
    }

    getAllHansynsytorForKontrakt(kontraktGuid: string): Geometri[] {

        // TODO: Subtyp2 för hänsynsytor?
        return this.geometrierCache
            .get(kontraktGuid)?.kontraktGeometrier.filter(x => x.isHansynsyta);
    }

    async getRitunderlagKoordinaterCache(dokumentId: number): Promise<VirkeskopRitunderlagKoordinaterDTO> {
        if (this.ritunderlagKoordinaterCache.has(dokumentId)) {
            return Promise.resolve(this.ritunderlagKoordinaterCache.get(dokumentId));
        } else {
            const response = await this.kontraktRitunderlagService.getRitunderlagKoordinater(dokumentId);
            this.ritunderlagKoordinaterCache.set(dokumentId, response);
            return response;
        }
    }

    highlightAtgarder(guid: string[], over: boolean) {
        this.highlight$.next({
            guid: guid,
            isOver: over
        });
    }

    highlightVirtualSkogsvardLayer(virtualLayer: ISkogsvardVirtualLayer) {
        const kontraktGuid = this.kontraktMediatorService.kontrakt.guid;
        const collection = this.getOrCreateCollection(kontraktGuid);
        const skogsvardsAtgarder = collection.skogsvardGeometrier
            .filter(g => g.atgardAtgardsgrupp === virtualLayer.group);
        this.highlightAtgarder(skogsvardsAtgarder.map(x => x.guid), virtualLayer.active);
    }

    hover(g: IMapGeometry, hoverIn = true): void {
        if (ObjectUtils.hasValue(g.id) && hoverIn === true) {
            this.atgardHoverIn$.next(g.id);
            return;
        }
        this.atgardHoverOut$.next(g.id);
    }

    initForKontrakt(kontrakt: Kontrakt): Observable<Geometrier> {
        const signal = (geometrier: Geometrier): Geometrier => {
            this.kontraktGeometrier$.next(geometrier.kontraktGeometrier);
            this.atgardGeometrier$.next(geometrier.atgardGeometrier);
            this.skogsvardGeometrier$.next(geometrier.skogsvardGeometrier);
            return geometrier;
        };

        // Nytt kontrakt
        if (ObjectUtils.isNullOrUndefined(kontrakt?.id)) {
            return of(this.getOrCreateCollection(kontrakt?.guid));
        }

        return this.avtalService.getGeometrierForKontrakt(kontrakt.id).pipe(map(dto => {
            const geometrier = this.convertToGeometrier(dto);
            this.setOrUpdateCollection(kontrakt.guid, geometrier);
            signal(geometrier);
            return geometrier;
        }));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openPopupGeometri(mapGeometry: IMapGeometry): void {
        this.popupGeometriSrc.next(mapGeometry);
    }

    selectSkogsvardGeometry(geometri: IMapGeometry) {
        this.selectedSkogsvardGeometrySrc.next(geometri);
    }

    setCacheForKontrakt(guid: string, geometrier: Geometrier) {
        this.geometrierCache.set(guid, geometrier);
    }

    updateAtgard(kontraktGuid: string, atgard: Atgard): void {
        const cache = this.geometrierCache.get(kontraktGuid);
        let geometri = cache?.atgardGeometrier.find(x => x.atgardGuid === atgard.guid);
        if (!geometri) {
            geometri = cache?.skogsvardGeometrier.find(x => x.atgardGuid === atgard.guid);
        }
        if (ObjectUtils.hasValue(geometri)) {
            geometri.updateFromAtgard(atgard);
        }
    }

    updateGeometries(geometryUpdates: GeometryUpdate[]) {
        geometryUpdates?.forEach(g => {
            this.updateGeometryInCacheAndSignal(g.kontraktGuid, g.atgardGuid, g.geometryId, g.wkt, g.layerId, g.metadata, true);
        });

        const kontraktGuids = uniq(geometryUpdates.map(x => x.kontraktGuid));
        kontraktGuids.forEach(x => {
            this.updateAllAtgardsarealer(geometryUpdates[0].kontraktGuid);
        });
    }

    updateGeometry(layerId: string, kontraktGuid: string, atgardGuid: string, geometryId: string, wkt: string, metadata?: any) {
        this.updateGeometryInCacheAndSignal(kontraktGuid, atgardGuid, geometryId, wkt, layerId, metadata, false);
    }

    updateGeometryByEvent(event: IKartgeometriEvent): void {
        this.updateGeometryInCacheAndSignal(event.payload.param.kontraktId,
            event.payload.param.atgardGuid,
            event.payload.param.geometryId,
            event.payload.geometri.geometri,
            event.payload.layer?.id ?? event.payload.geometri.subtyp1,
            event.payload.geometri);
    }

    private addAtgard(atgard: EntityDTO): void {
        const kontraktGuid = this.kontraktMediatorService.kontrakt.guid;
        const geometri = new Geometri("atgard", atgard.name, 0, kontraktGuid, null, null);
        geometri.atgardGuid = atgard.guid;
        geometri.atgardAtgardstyp = atgard.name;
        geometri.atgardAtgardsgrupp = "";
        geometri.atgardBeteckning = "";
        geometri.guid = atgard.guid;
        geometri.kontraktGuid = kontraktGuid;
        geometri.kartgeometri = null;
        geometri.mapGeometry = null;
        if (VKAtgardHelper.isAtgardEntitySkogsvard(atgard)) {
            geometri.parentType = "skogsvard";
            const geoms = this.geometrierCache.get(kontraktGuid).skogsvardGeometrier;
            geometri.index = geoms.length + 1;
            this.geometrierCache.get(kontraktGuid).skogsvardGeometrier.push(geometri);
        } else {
            const geoms = this.geometrierCache.get(kontraktGuid).atgardGeometrier;
            geometri.index = geoms.length + 1;
            this.geometrierCache.get(kontraktGuid).atgardGeometrier.push(geometri);
        }
        this.atgardAdded$.next(geometri);
    }

    private convertToGeometrier(dto: VKGeometrierDTO): Geometrier {
        return new Geometrier(dto);
    }

    private createAtgardGeometri(kontraktGuid: string, geometryId: any, wkt: string, metadata: IKartgeometri) {
        const collection = this.getOrCreateCollection(kontraktGuid);
        const geometri = new Geometri("atgard", "", 0, kontraktGuid);
        geometri.kartgeometri = metadata;
        geometri.atgardGuid = geometryId;
        geometri.mapGeometry = {
            visible: true,
            id: geometryId,
            wkt: wkt,
            isNew: true
        };
        collection.atgardGeometrier.push(geometri);
        return geometri;
    }

    // skapa och lägg ny geometri i cache
    private createKontraktGeometri(
        kontraktGuid: string,
        geometryId: any,
        geometriTyp: string,
        kartgeometri?: IKartgeometri,
        mapGeometry?: IMapGeometryWicket,
        atgardGuid = null): Geometri {
        const collection = this.getOrCreateCollection(kontraktGuid);
        let geo;
        if (!isNil(kartgeometri)) {
            geo = this.createKontraktGeometriFromKartgeometri(kontraktGuid, geometryId, geometriTyp, kartgeometri, atgardGuid);
        } else if (!isNil(mapGeometry)) {
            geo = this.createKontraktGeometriFromMapGeometry(kontraktGuid, geometryId, geometriTyp, mapGeometry, atgardGuid);
        }
        if (!isNil(atgardGuid)) {
            // add drivningspunkt to atgard in cache
            const atg = collection.atgardGeometrier.find(a => a.guid === atgardGuid);
            atg.addAtgardDrivningspunkt(geo);
        } else {
            // add kontraktgeometri to cache
            collection.kontraktGeometrier.push(geo);
        }
        return geo;
    }

    // Geometri skapad från collector-import
    private createKontraktGeometriFromKartgeometri(kontraktGuid: string, geometryId: any, geometriTyp: string, kartgeometri: IKartgeometri, atgardGuid = null): Geometri {
        const typ = isNil(atgardGuid) ? "kontrakt" : "";
        const geometri = new Geometri(typ, geometriTyp, 0, kontraktGuid, null);
        geometri.atgardGuid = atgardGuid;
        geometri.guid = geometryId;
        const mapGeometry = {
            id: geometryId,
            wkt: kartgeometri.geometri,
            visible: true,
            tag: geometri
        } as IMapGeometryWicket;
        geometri.setNewGeometri(mapGeometry, kartgeometri);
        return geometri;
    }

    // Geometri skapad från rita-menyn.
    private createKontraktGeometriFromMapGeometry(kontraktGuid: string, geometryId: any, geometriTyp: string, mapGeometry?: IMapGeometryWicket, atgardGuid = null): Geometri {
        const typ = isNil(atgardGuid) ? "kontrakt" : "";
        const geometri = new Geometri(typ, geometriTyp, 0, kontraktGuid, null, null);
        geometri.atgardGuid = atgardGuid;
        geometri.guid = geometryId;
        mapGeometry.id = geometryId;
        const kartgeometri = new Kartgeometri(geometriTyp, null, null, null, mapGeometry.wkt, true, null, null, null);
        geometri.setNewGeometri(mapGeometry, kartgeometri);
        return geometri;
    }

    private createSkogsvardGeometri(kontraktGuid: string, geometryId: any, wkt: string, metadata: IKartgeometri) {
        const collection = this.getOrCreateCollection(kontraktGuid);
        const geometri = new Geometri("skogsvard", "", 0, kontraktGuid);
        geometri.kartgeometri = metadata;
        geometri.atgardGuid = geometryId;
        geometri.mapGeometry = {
            visible: true,
            id: geometryId,
            wkt: wkt,
            isNew: true
        };
        collection.skogsvardGeometrier.push(geometri);
        return geometri;
    }

    private deleteGeometryFromCacheAndSignal(kontraktId: string, geometryId: any, layerId: string): void {
        let typ: GeometriParentType;

        switch (layerId) {
            case VkKartaLayerNames.virkeskopsatgardLayer:
                typ = "atgard";
                break;
            case VkKartaLayerNames.levvirkeLayer:
                typ = "leveransvirke";
                break;
            case VkKartaLayerNames.skogsvardatgardLayer:
                typ = "skogsvard";
                break;
            default:
                typ = "kontrakt";
        }

        this.findAndDelete(kontraktId, typ, geometryId).subscribe(geometri => {
            if (typ === "atgard" || typ === "leveransvirke" || typ === "skogsvard") {
                // atgards-geometri
                this.atgardGeometriDeleted$.next(geometri);
            } else if (typ === "kontrakt") {
                // kartgeometrier
                this.kontraktGeometriDeleted$.next(geometri);
                this.tryUpdateAtgardsarealer(geometri);
            }
        });
    }

    private findAndDelete(kontraktId: string, parentType: GeometriParentType, geometryId: any): Observable<Geometri> {
        let item: Geometri;
        const geometrier = this.getOrCreateCollection(kontraktId);
        if (parentType === "atgard" || parentType === "skogsvard") {
            item = this.findAtgardGeometri(kontraktId, null, geometryId);
            //atgard: clear geometry
            if (item) {
                item.areal = null;
                item.kartgeometri.geometri = null;
                item.mapGeometry.wkt = null;
            }
        } else if (parentType === "leveransvirke") {
            // atgard: remove entity
            const atgardGeometryCollection = geometrier.atgardGeometrier;
            for (const atgardGeometry of atgardGeometryCollection) {
                const drivningspunktGeom = atgardGeometry.atgardDrivningspunkter.find(y => y.guid === geometryId);
                if (drivningspunktGeom) {
                    const itemIndex = atgardGeometry.atgardDrivningspunkter.indexOf(item);
                    atgardGeometry.atgardDrivningspunkter.splice(itemIndex, 1);
                    item = drivningspunktGeom;
                    break;
                }
            }
        } else {
            // kontrakt: remove entity
            const collection = geometrier.kontraktGeometrier;
            item = collection.find(gs => gs.guid === geometryId);
            if (item) {
                const itemIdx = collection.indexOf(item);
                collection.splice(itemIdx, 1);
            }
        }
        return of(item);
    }

    private findAtgardDrivningspunktGeometri(kontraktGuid: string, atgardGuid: string, geometryId: string): Geometri {
        const collection = this.getOrCreateCollection(kontraktGuid);
        const flatlist = collection.atgardGeometrier.reduce((a, b) => a.concat(b.atgardDrivningspunkter), []);
        return flatlist.find(g => g.guid === geometryId);
    }

    private findAtgardGeometri(kontraktGuid: string, atgardGuid: string, geometryId: string): Geometri {
        const collection = this.getOrCreateCollection(kontraktGuid);
        let atgardGeom = collection.atgardGeometrier.find(x =>
            (ObjectUtils.hasValue(x.atgardGuid) && x.atgardGuid === atgardGuid) || ObjectUtils.hasValue(x.mapGeometry?.id) && x.mapGeometry.id === geometryId);
        if (!atgardGeom) {
            atgardGeom = collection.skogsvardGeometrier.find(x =>
                (ObjectUtils.hasValue(x.atgardGuid) && x.atgardGuid === atgardGuid) || ObjectUtils.hasValue(x.mapGeometry?.id) && x.mapGeometry.id === geometryId);
        }
        return atgardGeom;
    }

    private findKontraktGeometri(kontraktGuid: string, geometryId: string): Geometri {
        const collection = this.getOrCreateCollection(kontraktGuid);
        return collection.kontraktGeometrier.find(x => x.mapGeometry.id === geometryId);
    }

    private getOrCreateCollection(kontraktGuid: string): Geometrier {
        if (this.geometrierCache.has(kontraktGuid)) {
            return this.geometrierCache.get(kontraktGuid);
        } else {
            const geometrier: Geometrier = {
                kontraktGuid: kontraktGuid,
                atgardGeometrier: [],
                kontraktGeometrier: [],
                skogsvardGeometrier: []
            };
            this.geometrierCache.set(kontraktGuid, geometrier);
            return geometrier;
        }
    }

    private isChanged(geometri: Geometri, wkt: string, atgardGuid: string, kartgeometri: IKartgeometri): boolean {
        return this.isGeometryChanged(geometri, wkt, atgardGuid, kartgeometri) ||
            this.isMetadataChanged(geometri, kartgeometri);
    }

    private isGeometryChanged(geometri: Geometri, wkt: string, atgardGuid: string, kartgeometri: IKartgeometri): boolean {
        return geometri.mapGeometry.wkt !== wkt || geometri.atgardGuid !== atgardGuid ||
            geometri.kartgeometri !== kartgeometri || geometri.kartgeometri.geometri !== wkt;
    }

    private isMetadataChanged(fromCache: Geometri, fromMap: IKartgeometri): boolean {
        return fromCache.kartgeometri.anteckning !== fromMap.persistedKartgeometri?.anteckning ||
            fromCache.kartgeometri.subtyp1 !== fromMap.persistedKartgeometri?.subtyp1 ||
            fromCache.kartgeometri.subtyp2 !== fromMap.persistedKartgeometri?.subtyp2 ||
            fromCache.kartgeometri.subtyp3 !== fromMap.persistedKartgeometri?.subtyp3 ||
            fromCache.kartgeometri.karttext !== fromMap.persistedKartgeometri?.karttext ||
            fromCache.kartgeometri.tillganglighetsklass !== fromMap.persistedKartgeometri?.tillganglighetsklass ||
            fromCache.kartgeometri.hansynAtgardTyp !== fromMap.persistedKartgeometri?.hansynAtgardTyp;
    }

    private recalcHektar(geometri: Geometri): void {
        const wkt = geometri.mapGeometry?.wkt ?? geometri.kartgeometri.geometri;
        const kontraktGuid = geometri.kontraktGuid;
        const hansynsytor = this.getAllHansynsytorForKontrakt(kontraktGuid).map(g => g.mapGeometry.wkt);
        const areal = this.spatialService.getNetAreaHectares(wkt, hansynsytor);
        geometri.areal = areal;
        this.ngZone.run(() => {
            this.atgardGeometriChanged$.next(geometri);
        });

    }

    private setAtgarder(atgarder: Atgard[]) {
        this.atgarder = atgarder;
    }

    private setOrUpdateCollection(kontraktGuid: string, geometrier: Geometrier) {
        const collection = this.getOrCreateCollection(kontraktGuid);
        collection.atgardGeometrier = geometrier.atgardGeometrier;
        collection.kontraktGeometrier = geometrier.kontraktGeometrier;
        collection.skogsvardGeometrier = geometrier.skogsvardGeometrier;
    }

    private tryUpdateAtgardsarealer(geometri: Geometri) {
        if (geometri?.isHansynsyta) {
            this.updateAllAtgardsarealer(geometri.kontraktGuid);
        }
    }

    private updateAllAtgardsarealer(kontraktGuid: string): void {
        const collection = this.getOrCreateCollection(kontraktGuid);
        const atgarderGeometrier = collection.atgardGeometrier.concat(collection.skogsvardGeometrier);
        atgarderGeometrier.forEach(atggeo => {
            const atgard = this.atgarder?.find(a => a.guid === atggeo.atgardGuid);
            if (ObjectUtils.isNullOrUndefined(atgard)) {
                console.warn(`UpdateAtgardsarealer: Atgärd för geometri ${atggeo.guid} hittades inte!`);
            } else {
                if (!atgard.isLeveransvirke &&
                    !atgard.isUtfort &&
                    !atgard.isTillgangligt &&
                    !isNil(atggeo.mapGeometry?.wkt)) {
                    this.recalcAreaSrc.next(atggeo);
                }
            }
        });
    }

    private updateAtgardDrivningspunktInCacheAndSignal(kontraktGuid: string, atgardGuid: string, geometryId: any, wkt: string, kartgeometri: IKartgeometri) {
        let geometri = this.findAtgardDrivningspunktGeometri(kontraktGuid, atgardGuid, geometryId);
        if (geometri) {
            // update
            if (this.isChanged(geometri, wkt, atgardGuid, kartgeometri)) {
                geometri.mapGeometry.wkt = wkt;
                geometri.atgardGuid = atgardGuid;
                geometri.kartgeometri = kartgeometri;
                geometri.kartgeometri.geometri = wkt;
            }
            this.atgardDrivningspunktChanged$.next(geometri);
            return of(geometri);
        } else {
            // create
            kartgeometri.geometri = wkt;
            geometri = this.createKontraktGeometri(kontraktGuid, geometryId, kartgeometri.subtyp1, kartgeometri, undefined, atgardGuid);
            this.atgardDrivningspunktAdded$.next(geometri);
            return of(geometri);
        }
    }

    private updateAtgardGeometryInCacheAndSignal(kontraktGuid: string, atgardGuid: string, geometryId: any, wkt: string, metadata?: IKartgeometri): void {
        let geometri = this.findAtgardGeometri(kontraktGuid, atgardGuid, geometryId);
        if (!isNil(geometri)) {
            // update
            const nyKartgeometri = new Kartgeometri(metadata?.subtyp1, metadata?.subtyp2, metadata?.subtyp3, metadata?.anteckning, wkt, true);
            nyKartgeometri.barighet = metadata?.barighet;
            nyKartgeometri.ytstruktur = metadata?.ytstruktur;
            nyKartgeometri.lutning = metadata?.lutning;
            nyKartgeometri.importkalla = metadata?.importkalla;
            nyKartgeometri.direktutbyteTradslag = metadata?.direktutbyteTradslag;

            geometri.kartgeometri = nyKartgeometri;
            if (isNil(geometri.mapGeometry)) {
                geometri.setNewGeometri(
                    {
                        visible: true,
                        id: geometryId,
                        wkt: wkt,
                        isNew: true,
                        styleName: "atgardYta",
                        tag: metadata
                    },
                    geometri.kartgeometri
                );
            } else {
                geometri.mapGeometry.wkt = wkt;
                geometri.mapGeometry.id = geometryId;
            }
            this.recalcAreaSrc.next(geometri);
        } else {
            // create
            geometri = this.createAtgardGeometri(kontraktGuid, geometryId, wkt, metadata);
            this.atgardGeometriAdded$.next(geometri);
            this.recalcAreaSrc.next(geometri);
        }
    }

    private updateAtgardSkogsvardGeometryInCacheAndSignal(kontraktGuid: string, atgardGuid: string, geometryId: any, wkt: string, metadata: IKartgeometri) {
        let geometri = this.findAtgardGeometri(kontraktGuid, atgardGuid, geometryId);
        if (!isNil(geometri)) {
            // update
            const nyKartgeometri = new Kartgeometri(metadata?.subtyp1, metadata?.subtyp2, metadata?.subtyp3, metadata?.anteckning, wkt, true);

            geometri.kartgeometri = nyKartgeometri;
            if (isNil(geometri.mapGeometry)) {
                geometri.setNewGeometri(
                    {
                        visible: true,
                        id: geometryId,
                        wkt: wkt,
                        isNew: true,
                        styleName: "atgardYta",
                        tag: metadata
                    },
                    geometri.kartgeometri
                );
            } else {
                geometri.mapGeometry.wkt = wkt;
                geometri.mapGeometry.id = geometryId;
            }
            this.recalcAreaSrc.next(geometri);
        } else {
            // create
            geometri = this.createSkogsvardGeometri(kontraktGuid, geometryId, wkt, metadata);
            this.atgardGeometriAdded$.next(geometri);
            this.recalcAreaSrc.next(geometri);
        }
    }

    // eslint-disable-next-line max-len
    private updateGeometryInCacheAndSignal(kontraktGuid: string, atgardGuid: string, geometryId: any, wkt: string, layerid: string, metadata?: IKartgeometri, supressRecalc = false): void {
        if (layerid === VkKartaLayerNames.virkeskopsatgardLayer) {
            this.updateAtgardGeometryInCacheAndSignal(kontraktGuid, atgardGuid, geometryId, wkt, metadata);
        } else if (layerid === VkKartaLayerNames.levvirkeLayer) {
            this.updateAtgardDrivningspunktInCacheAndSignal(kontraktGuid, atgardGuid, geometryId, wkt, metadata);
        } else if (layerid === VkKartaLayerNames.skogsvardatgardLayer) {
            this.updateAtgardSkogsvardGeometryInCacheAndSignal(kontraktGuid, atgardGuid, geometryId, wkt, metadata);
        } else {
            // kontrakt
            this.updateKontraktGeometryInCacheAndSignal(kontraktGuid, geometryId, wkt, metadata, supressRecalc);
        }
    }

    // uppdatera eller skapa ny geometri, uppdatera cache och signalera förändring
    private updateKontraktGeometryInCacheAndSignal(kontraktGuid: string, geometryId: any, wkt: string, kartgeometri: IKartgeometri, supressRecalc = false) {
        let geometri = this.findKontraktGeometri(kontraktGuid, geometryId);
        if (geometri) {
            if (this.isChanged(geometri, wkt, undefined, kartgeometri)) {
                geometri.mapGeometry.wkt = wkt;
                geometri.kartgeometri = kartgeometri;
                geometri.kartgeometri.geometri = wkt;
                this.kontraktGeometriChanged$.next(geometri);
            }
            // update
            if (!supressRecalc) {
                this.tryUpdateAtgardsarealer(geometri);
            }
            return of(geometri);
        } else {
            // create
            kartgeometri.geometri = wkt;
            geometri = this.createKontraktGeometri(kontraktGuid, geometryId, kartgeometri.subtyp1, kartgeometri);
            this.kontraktGeometriAdded$.next(geometri);
            if (!supressRecalc) {
                this.tryUpdateAtgardsarealer(geometri);
            }
            return of(geometri);
        }
    }
}
